/**
 * Shared UI component for UI Sidebar.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { Component, input, output, signal } from '@angular/core';
import { IsActiveMatchOptions, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { UiSidebarNavItem, UiSidebarUser } from './ui-sidebar.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Sidebar
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe, UiLabelTranslatePipe],
  templateUrl: './ui-sidebar.component.html',
  styleUrls: ['./ui-sidebar.component.scss']
})
export class UiSidebarComponent {
  /**
   * ROUTING RULE: top-level modules stay highlighted across module-owned tabs
   * and nested routes so sidebar focus remains stable inside a feature area.
   */
  readonly topLevelActiveMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    matrixParams: 'ignored',
    fragment: 'ignored'
  };
  readonly childActiveMatchOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'exact',
    matrixParams: 'ignored',
    fragment: 'ignored'
  };
  readonly items = input<UiSidebarNavItem[]>([]);
  /** PARENT-CHILD CONTRACT: collapsed documents the input state boundary used by this file. */
  readonly collapsed = input(false);
  /** STATE OWNER: loadedAvatarSrc documents the signal state boundary used by this file. */
  readonly loadedAvatarSrc = signal('');
  readonly user = input<UiSidebarUser>({
    name: 'Demo User',
    role: 'Admin',
    initials: 'DU'
  });
  readonly collapsedChange = output<boolean>();
  readonly expandedGroups = new Set<string>();
  readonly fallbackIcons: Record<string, string> = {
    dashboard: 'dashboard',
    agencies: 'apartment',
    supervisors: 'group',
    agents: 'badge',
    pos: 'point_of_sale',
    inventory: 'inventory_2',
    btl: 'campaign',
    logistics: 'local_shipping',
    reports: 'bar_chart',
    alerts: 'notifications_active',
    configurations: 'settings',
    roles: 'security',
    profile: 'person',
    component: 'dashboard_customize'
  };

  /**
   * BUSINESS LOGIC: toggleCollapsed
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleCollapsed(): void {
    this.collapsedChange.emit(!this.collapsed());
  };

  /**
   * BUSINESS LOGIC: isAvatarLoaded
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isAvatarLoaded(src?: string): boolean {
    const avatarSrc = src?.trim() ?? '';
    return !!avatarSrc && this.loadedAvatarSrc() === avatarSrc;
  }

  /**
   * BUSINESS LOGIC: onAvatarLoaded
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onAvatarLoaded(): void {
    this.loadedAvatarSrc.set(this.user().avatar?.trim() ?? '');
  }

  /**
   * BUSINESS LOGIC: onAvatarError
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onAvatarError(): void {
    this.loadedAvatarSrc.set('');
  }

  /**
   * BUSINESS LOGIC: onLogoClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onLogoClick(): void {
    if (this.collapsed()) {
      this.collapsedChange.emit(false);
    }
  }

  /**
   * BUSINESS LOGIC: onGroupParentClick
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onGroupParentClick(path: string): void {
    if (this.collapsed()) {
      this.collapsedChange.emit(false);
    }

    this.toggleGroup(path);
  }

  /**
   * BUSINESS LOGIC: toggleGroup
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleGroup(path: string): void {
    if (this.expandedGroups.has(path)) {
      this.expandedGroups.delete(path);
      return;
    }

    this.expandedGroups.add(path);
  }

  /**
   * BUSINESS LOGIC: isGroupExpanded
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isGroupExpanded(path: string): boolean {
    return this.expandedGroups.has(path);
  }

  /**
   * Keeps sidebar DOM identity stable for same-path entries that differ only by
   * query params, such as Configurations child links.
   */
  trackNavItem(item: UiSidebarNavItem): string {
    const queryKey = Object.entries(item.queryParams ?? {})
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    return `${item.path}|${queryKey}|${item.labelKey ?? item.label}`;
  }

  /**
   * BUSINESS LOGIC: resolveItemIcon
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  resolveItemIcon(item: UiSidebarNavItem): string {
    if (item.iconSymbol) {
      return item.iconSymbol;
    }

    const pathKey = item.labelKey?.replace('sidebar.', '').trim();

    if (pathKey && this.fallbackIcons[pathKey]) {
      return this.fallbackIcons[pathKey];
    }

    if (item.label) {
      const lowerLabel = item.label.toLowerCase();

      if (lowerLabel.includes('dashboard')) {
        return 'dashboard';
      }

      if (lowerLabel.includes('component')) {
        return 'dashboard_customize';
      }

      if (lowerLabel.includes('configuration')) {
        return 'settings';
      }

      if (lowerLabel.includes('report')) {
        return 'bar_chart';
      }
    }

    return 'article';
  }
}
